import React from 'react';
import YesIcon from '../../../../Assets/Prepratory/picsvg_download (17).svg';
import SometimesIcon from '../../../../Assets/Prepratory/picsvg_download (18).svg';
import NoIcon from '../../../../Assets/Prepratory/picsvg_download (19).svg';
import NotSureIcon from '../../../../Assets/Prepratory/picsvg_download (20).svg';


/**
 * PDF-compatible checkbox styles - using inline styles and div-based checkboxes
 * for reliable html2canvas rendering
 */
const getCheckboxStyle = (checked) => ({
    width: '16px',
    height: '16px',
    minWidth: '16px',
    minHeight: '16px',
    border: '2px solid #374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: checked ? '#374151' : '#ffffff',
    marginRight: '8px'
});

/**
 * PDF-compatible selection border style helper
 */
const getSelectionBorderStyle = (selected) => {
    if (selected) {
        return {
            border: '3px solid #F97316',
            borderRadius: '9999px',
            backgroundColor: '#FEF3C7',
            boxShadow: 'none'
        };
    }
    return {
        border: '2px solid #D1D5DB',
        borderRadius: '9999px',
        backgroundColor: '#F3F4F6'
    };
};

/**
 * Renders a single resource item with its icon, label, and a styled checkbox.
 * Uses div-based checkbox for PDF compatibility.
 */
const ResourceItem = ({ label, checked, children }) => (
    <div className="flex flex-col items-center text-center">
        <div className="h-12 w-16 flex items-center justify-center text-2xl mb-1">
            {children}
        </div>
        <label className="text-xs font-medium h-8 flex items-center text-center leading-tight">{label}</label>
        <div style={getCheckboxStyle(checked)}>
            {checked && <span style={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}>✓</span>}
        </div>
    </div>
);

/**
 * Renders a single question row in the "Understanding of my Child" table.
 * Uses inline styles and fixed pixel sizes for PDF compatibility.
 */
const UnderstandingQuestionRow = ({ text, value }) => {
    const options = [
        { label: 'Yes', icon: YesIcon },
        { label: 'Sometimes', icon: SometimesIcon },
        { label: 'No', icon: NoIcon },
        { label: 'Not sure', icon: NotSureIcon }
    ];

    return (
        <tr className="border-b border-gray-300">
            <td className="p-3 text-sm border-r border-gray-300" style={{ width: '60%' }}>{text}</td>
            <td className="p-2">
                <div className="grid grid-cols-4 gap-2">
                    {options.map(({ label, icon }) => {
                        const isSelected = value === label;
                        return (
                            <div key={label} className="flex flex-col items-center">
                                <div
                                    style={{
                                        ...getSelectionBorderStyle(isSelected),
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img
                                        src={icon}
                                        alt={label}
                                        style={{ width: '32px', height: '32px' }}
                                    />
                                </div>
                                <div className="text-xs mt-1 text-center">{label}</div>
                            </div>
                        );
                    })}
                </div>
            </td>
        </tr>
    );
};

// --- Main Page Component ---

const CaregiverObservationPage = ({ assignment }) => {
    // Safely access nested caregiver_observation data
    const safeData = assignment?.caregiver_observation || {};

    // Define the resources and their icons to match the reference
    const resourceOptions = [
        { id: "Books and Magazines", icon: "📚", label: "Books and\nMagazines" },
        { id: "Newspapers", icon: "📰", label: "Newspapers" },
        { id: "Toys, Games and sports", icon: "🎮", label: "Toys, Games and\nsports" },
        { id: "Phone and Computer", icon: "💻", label: "Phone and\nComputer" },
        { id: "Internet", icon: "🌐", label: "Internet" },
        {
            id: "Public Broadcast System",
            icon: "📻",
            label: "Public Broadcast\nSystem\n(audio/video at the panchayat\nand block level)"
        },
        { id: "Resources for CWSN", icon: "♿", label: "Resources for\nCWSN" }
    ];

    // Define the questions for the understanding section
    const understandingQuestions = [
        {
            key: "motivated_to_learn",
            text: "1. My child seems motivated to learn and engage with new concepts learnt at school."
        },
        {
            key: "follows_schedule",
            text: "2. My child follows a schedule at home that includes curriculum and other activities, social connectivity, and screen time."
        },
        {
            key: "finds_curriculum_difficult",
            text: "3. My child finds the grade-level curriculum difficult and needs additional support."
        },
        {
            key: "making_good_progress",
            text: "4. My child is making good progress as per his/her grade."
        }
    ];

    // Define the areas where the child might need support - matching reference exactly
    const supportNeedsOptions = [
        "Languages (R1, R2, R3)", "Mathematics", "Science", "Social Science",
        "Building self-belief & self-reliance", "Developing social skills & conflict resolution",
        "Managing difficult emotions like anger", "Developing effective study skills like time management",
        "Skill Guidance/Digital Literacy"
    ];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.6',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            <div className="relative z-10">
                {/* Headers */}
                <h2 className="text-center font-bold text-xl text-orange-600 mb-1">PART A (4)</h2>
                <h3 className="text-center font-semibold text-lg text-gray-700 mb-1">Parent-Teacher Partnership
                    Card</h3>

                {/* Resources Section */}
                <div className="mb-1">
                    <p className="font-semibold text-sm mb-3">Tick the resources available to your child at home.</p>
                    <div className="grid grid-cols-7 gap-2 mb-3">
                        {resourceOptions.map(res => (
                            <ResourceItem
                                key={res.id}
                                label={res.label}
                                checked={(safeData.resources || []).includes(res.id)}
                            >
                                {res.icon}
                            </ResourceItem>
                        ))}
                    </div>
                    <div className="flex items-baseline text-sm">
                        <span className="font-semibold mr-2">Any other (please specify)</span>
                        <span className="border-b border-gray-400 flex-1 pb-1 min-h-[1em]">
                            {safeData.other_resources || ''}
                        </span>
                    </div>
                </div>

                {/* Understanding of my Child Section */}
                <div className="mb-1">
                    <div className="bg-orange-500 text-white font-bold text-center py-2 text-sm">
                        Understanding of my Child
                    </div>
                    <div className="border-2 border-gray-300">
                        <div className="bg-orange-100 p-2 border-b border-gray-300">
                            <span
                                className="text-sm font-medium">Circle the most appropriate option for your child.</span>
                        </div>
                        <table className="w-full">
                            <tbody>
                                {understandingQuestions.map(q => (
                                    <UnderstandingQuestionRow
                                        key={q.key}
                                        text={q.text}
                                        value={safeData.understanding?.[q.key]}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Support Needs Section */}
                <table className="w-full border mb-1 border-orange-400 text-sm bg-white">
                    <tbody>
                        <tr>
                            <td className="p-2">
                                <div className="bg-orange-500 text-white font-bold text-center py-2 text-sm mb-2">
                                    At school, my child needs support with:
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                                    {supportNeedsOptions.map(opt => {
                                        const isChecked = (safeData.school_support_needs || []).includes(opt);
                                        return (
                                            <div key={opt} className="flex items-center">
                                                <div style={getCheckboxStyle(isChecked)}>
                                                    {isChecked && <span style={{ color: 'white', fontSize: '10px' }}>✓</span>}
                                                </div>
                                                <span>{opt}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex items-baseline text-sm">
                                    <span className="font-semibold mr-2">Any other</span>
                                    <span className="border-b border-gray-400 flex-1 pb-1 min-h-[1em]">
                                        {safeData.other_school_support || ''}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Home Support Commitment Section */}
                <div className="flex-grow">
                    <div className="bg-orange-500 text-white font-bold py-2 px-3 text-sm">
                        Based on my discussion with the teacher, I will support my child at home by:
                    </div>
                    <div className="bg-orange-100 border-2 border-orange-500 border-t-0 p-3 min-h-[80px] text-sm">
                        {safeData.home_support_plan || ''}
                    </div>
                    <div
                        className="bg-orange-100 p-2 text-xs italic text-gray-700 border-2 border-orange-500 border-t-0">
                        (Fostering a strong parent-teacher partnership is essential to ensure your child's holistic
                        development.
                        While teachers support at school, please use this space to write how can you provide additional
                        support to your child at home.)
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CaregiverObservationPage;